import { createSelector } from 'reselect';
import lodash from 'lodash';
import { getSIPStatusPermission } from 'common/permissions/selectors';
import { calculateAutonameValue } from 'indexDocuments/util/autoname';

const SIP_READY_STATUS_SYSTEM_NAME = 'sip_ready_status';
const getDocument = store => lodash.get(store, 'indexDocument.document');
const getIndexingType = store => lodash.get(store, 'indexDocument.indexingType');
const getSelectedDocumentTypeId = store => lodash.get(store, 'indexDocument.selectedDocumentTypeId');
const getDocumentTypes = store => lodash.get(store, 'indexDocument.documentTypes');
const getDocumentTypeFilter = store => lodash.get(store, 'indexDocument.documentTypeFilter');
const getSelectedDescriptors = store => lodash.get(store, 'indexDocument.selectedDescriptors');
const getHighlightedDocumentTypeId = store => lodash.get(store, 'indexDocument.highlightedDocumentTypeId');
const getDescriptorValues = store => lodash.get(store, 'indexDocument.descriptorValues');
const getSaving = store => lodash.get(store, 'indexDocument.saving');
const getAscertainmentLoading = store => lodash.get(store, 'indexDocument.ascertainmentLoading');
const getCanRestore = store => lodash.get(store, 'indexDocument.canRestore');
const getBinderDocumentTypeIds = store => lodash.map(lodash.get(store, 'binder.layout.documentTypes', []), 'id');
const getBinderIndexingMetadataDescriptorValues = store => lodash.get(store, 'indexDocument.binderIndexingMetadata.descriptorValues');
const getBinderIndexingSingleValueDescriptorValues = store => lodash.get(store, 'indexDocument.binderIndexingMetadata.singleValueDescriptorValues');
const getInstitutionTimezone = store => lodash.get(store, 'me.institutionTimezone');

export const getDescriptors = createSelector(
    [getSelectedDescriptors,
        getDescriptorValues,
        getSIPStatusPermission,
        getBinderIndexingMetadataDescriptorValues,
        getBinderIndexingSingleValueDescriptorValues],
    (selectedDescriptors,
        descriptorValues,
        canManageSIPStatus,
        binderIndexingMetadataDescriptorValues,
        binderIndexingSingleValueDescriptorValues) => {
        return lodash(selectedDescriptors)
            .sortBy(['displayOrder', 'displayName'])
            .filter((descriptor) => descriptor.systemName !== SIP_READY_STATUS_SYSTEM_NAME || canManageSIPStatus)
            .map((descriptor, index) => {
                const editorData = descriptorValues[descriptor.id];
                const setupValues = lodash.get(binderIndexingMetadataDescriptorValues, descriptor.id, null);
                const singleValueDescriptorValues = lodash.values(binderIndexingSingleValueDescriptorValues);
                return {
                    descriptor: descriptor,
                    order: index,
                    setupValues,
                    ...editorData,
                    singleValueDescriptorValues
                };
            })
            .keyBy('descriptor.id')
            .value();
    },
);

export const getSelectedDocumentType = createSelector(
    [getSelectedDocumentTypeId, getDocumentTypes],
    (selectedDocumentTypeId, documentTypes) => documentTypes[selectedDocumentTypeId]);

export const getAutonameValue = createSelector(
    [getDocument, getSelectedDocumentType, getDescriptors, getInstitutionTimezone],
    (document, documentType, descriptors, institutionTimezone) => {
        return calculateAutonameValue({
            document,
            documentType,
            descriptors: lodash.map(descriptors, descriptorValues => {
                const { descriptor, values } = descriptorValues;
                return {
                    ...descriptor,
                    value: lodash.map(values, ({ value }) => value),
                };
            }),
            institutionTimezone
        });
    });

export const getFilteredDocumentTypes = createSelector(
    [getDocumentTypes, getDocumentTypeFilter, getIndexingType, getBinderDocumentTypeIds],
    (documentTypes, filter, indexingType, binderDocumentTypeIds) => {
        const hasBinderDocumentIds = lodash.size(binderDocumentTypeIds) > 0;
        let documentTypeChain;
        if (lodash.isEmpty(filter)) {
            documentTypeChain = lodash.chain(documentTypes);
        } else {
            const pattern = lodash.escapeRegExp(filter);
            const spaceInsensitivePattern = pattern.replace(/\s+/, '\\s+');
            const tester = new RegExp(spaceInsensitivePattern, 'i');
            documentTypeChain = lodash.chain(documentTypes)
                .filter(dt => {
                    return tester.test(dt.name) ||
                        tester.test(dt.displayName) ||
                        tester.test(dt.description);
                });
        }
        return documentTypeChain
            .filter(dt => {
                if (hasBinderDocumentIds && !binderDocumentTypeIds.includes(dt.id)) {
                    return false;
                }
                const permissions = JSON.parse(dt.permissions);
                const indexingPermission = lodash.find(permissions, (permission, key) => key.toLowerCase() === indexingType.toLowerCase());
                return indexingPermission === true;
            })
            .sortBy('displayName')
            .keyBy('id')
            .value();
    },
);

export const getHighlightedDocumentType = createSelector(
    [getDocumentTypes, getHighlightedDocumentTypeId],
    (documentTypes, documentTypeId) => lodash.get(documentTypes, documentTypeId, null),
);

export const calculateSavingFlag = createSelector(
    [getSaving, getAscertainmentLoading],
    (saving, ascertainmentLoading) => saving || ascertainmentLoading,
);

export const calculateSaveButtonDisableState = createSelector(
    [getSelectedDocumentType, getDescriptors, calculateSavingFlag],
    (documentType, descriptors, saving) => {
        if (saving) {
            return {
                disabled: true,
                message: 'Please wait for the save operation to finish.',
            };
        }
        if (!documentType) {
            return {
                disabled: true,
                message: 'Document type must be selected.',
            };
        }

        const isMissingRequiredDescriptor = lodash.some(descriptors, ({
            descriptor: { isRequiredForDocumentType: isRequired },
            values,
        }) => {
            if (!isRequired) {
                return false;
            }
            return lodash.isEmpty(values) || lodash.some(values,
                ({ value }) => lodash.isNil(value) || (lodash.isString(value) && lodash.isEmpty(value)));
        });

        if (isMissingRequiredDescriptor) {
            return {
                disabled: true,
                message: 'At least one required descriptor needs a value.',
            };
        }

        const hasInvalidExistingValue = lodash.some(descriptors, ({ values }) =>
            lodash.some(values, ({ invalidValue }) => invalidValue));

        if (hasInvalidExistingValue) {
            return {
                disabled: true,
                message: 'At least one value is invalid and needs to be corrected.',
            };
        }

        return {
            disabled: false,
            message: '',
        };
    },
);

export const calculateRestoreButtonVisibility = createSelector(
    [getIndexingType, getCanRestore],
    (indexingType, canRestore) => indexingType === 'initialIndex' && canRestore,
);

export const calculateFileAndSendButtonVisibility = createSelector(
    [getDocumentTypes, getSelectedDocumentTypeId],
    (documentTypes, selectedDocumentTypeId) => {
        if(lodash.isNil(selectedDocumentTypeId)){
            return false;
        }
        const documentType = lodash.get(documentTypes, selectedDocumentTypeId);
        return lodash.get(JSON.parse(documentType.permissions),'sdfa', false);
    }
);
